import { Routes, Route, Navigate } from 'react-router'
import { lazy, Suspense } from 'react'
import { Spin } from 'antd'
import { ProtectedRoute } from './ProtectedRoute'
import { PermissionRoute } from './PermissionRoute'
import AdminLayout from '@/components/AdminLayout'

// Loading 组件
const Loading = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh'
  }}>
    <Spin size="large" />
  </div>
)

// 懒加载页面
const Login = lazy(() => import('@/pages/login'))
const Dashboard = lazy(() => import('@/pages/dashboard'))
const Users = lazy(() => import('@/pages/users'))
const UserDetail = lazy(() => import('@/pages/users/detail'))
const AttributionCoverage = lazy(() => import('@/pages/users/attribution-coverage'))
const UserRetention = lazy(() => import('@/pages/users/retention'))
const UserGeo = lazy(() => import('@/pages/users/geo'))
const CurrencyGrants = lazy(() => import('@/pages/users/currency-grants'))
const Reports = lazy(() => import('@/pages/reports'))
const TemplateReports = lazy(() => import('@/pages/template-reports'))

// 角色管理
const Characters = lazy(() => import('@/pages/characters'))
const CharacterAudit = lazy(() => import('@/pages/characters/audit'))
const CharacterCategories = lazy(() => import('@/pages/characters/categories'))
const CharacterNewCharacters = lazy(() => import('@/pages/characters/new-characters'))
const CharacterTags = lazy(() => import('@/pages/characters/tags'))
const TemplateVariables = lazy(() => import('@/pages/characters/template-variables'))
const SystemTemplateVariables = lazy(() => import('@/pages/characters/system-template-variables'))
const CharacterStats = lazy(() => import('@/pages/characters/stats'))
const CharacterAIConfigs = lazy(() => import('@/pages/characters/ai-configs'))
const CharacterDetail = lazy(() => import('@/pages/characters/detail'))

// 支付管理
const Products = lazy(() => import('@/pages/payment/products'))
const Orders = lazy(() => import('@/pages/payment/orders'))
const Subscriptions = lazy(() => import('@/pages/payment/subscriptions'))
const Refunds = lazy(() => import('@/pages/payment/refunds'))
const Fulfillments = lazy(() => import('@/pages/payment/fulfillments'))
const WebhookLogs = lazy(() => import('@/pages/payment/webhook-logs'))
const PaymentEvents = lazy(() => import('@/pages/payment/payment-events'))
const PaymentAnalytics = lazy(() => import('@/pages/payment/analytics'))
const PaymentLTV = lazy(() => import('@/pages/payment/ltv'))
const CohortOrders = lazy(() => import('@/pages/payment/cohort-orders'))
const ApplePayConfigs = lazy(() => import('@/pages/payment/apple-pay-configs'))
const GooglePayConfigs = lazy(() => import('@/pages/payment/google-pay-configs'))
const AppsFlyerConfigs = lazy(() => import('@/pages/payment/appsflyer-configs'))
const ThinkingDataConfigs = lazy(() => import('@/pages/payment/thinkingdata-configs'))
const PaymentChannels = lazy(() => import('@/pages/payment/payment-channels'))
const PaymentChannelGroups = lazy(() => import('@/pages/payment/payment-channel-groups'))
const DiamondOverview = lazy(() => import('@/pages/diamond/overview'))
const DiamondCohort = lazy(() => import('@/pages/diamond/cohort'))
const DiamondPaymentAnalysis = lazy(() => import('@/pages/diamond/payment-analysis'))
const DiamondLedger = lazy(() => import('@/pages/diamond/ledger'))

// 运营管理
const Activities = lazy(() => import('@/pages/operations/activities'))
const ActivitySeries = lazy(() => import('@/pages/operations/activity-series'))
const Announcements = lazy(() => import('@/pages/operations/announcements'))
const Notifications = lazy(() => import('@/pages/operations/notifications'))
const InboxMessages = lazy(() => import('@/pages/operations/inbox-messages'))
const Tasks = lazy(() => import('@/pages/operations/tasks'))
const Checkin = lazy(() => import('@/pages/operations/checkin'))
const CheckinPlans = lazy(() => import('@/pages/operations/checkin-plans'))
const CheckinStats = lazy(() => import('@/pages/operations/checkin-stats'))
const Invitation = lazy(() => import('@/pages/operations/invitation'))
const EmailBindReward = lazy(() => import('@/pages/operations/email-bind-reward'))
const RegisterBonus = lazy(() => import('@/pages/operations/register-bonus'))
const Vip = lazy(() => import('@/pages/operations/vip'))
const TrendingKeywords = lazy(() => import('@/pages/operations/trending-keywords'))
const TrendingKeywordStats = lazy(() => import('@/pages/operations/trending-keyword-stats'))
const CharacterGeneration = lazy(() => import('@/pages/operations/character-generation'))
const SmartReplyConfigs = lazy(() => import('@/pages/operations/smart-reply-configs'))
const EmotionPhotoConfigs = lazy(() => import('@/pages/operations/emotion-photo-configs'))
const SmartReplyPresets = lazy(() => import('@/pages/operations/smart-reply-presets'))
const Rewards = lazy(() => import('@/pages/operations/rewards'))
const ScheduledTasks = lazy(() => import('@/pages/operations/scheduled-tasks'))

// 系统设置
const Apps = lazy(() => import('@/pages/settings/apps'))
const AppSettings = lazy(() => import('@/pages/settings/app-settings'))
const Versions = lazy(() => import('@/pages/settings/versions'))
const Firebase = lazy(() => import('@/pages/settings/firebase'))
const ServiceAccounts = lazy(() => import('@/pages/settings/service-accounts'))
const BaseLimits = lazy(() => import('@/pages/settings/base-limits'))
const AlertBots = lazy(() => import('@/pages/settings/alert-bots'))
const AppStoreLinks = lazy(() => import('@/pages/settings/app-store-links'))
const ComfyuiServers = lazy(() => import('@/pages/settings/comfyui-servers'))
const JimengConfigs = lazy(() => import('@/pages/settings/jimeng-configs'))
const GenerationV2 = lazy(() => import('@/pages/generation-v2'))
const TTSVoices = lazy(() => import('@/pages/tts-voices'))
const GenerationStats = lazy(() => import('@/pages/generation-stats'))
const GenerationAnalytics = lazy(() => import('@/pages/generation-analytics'))
const AmspireLibrary = lazy(() => import('@/pages/amspire-library'))
const AmspireAdmin = lazy(() => import('@/pages/amspire'))
const AmspireSectionRequests = lazy(() => import('@/pages/amspire-section-requests'))
const OnelinkConfigs = lazy(() => import('@/pages/settings/onelink-configs'))
const TranslationConfigs = lazy(() => import('@/pages/settings/translation-configs'))
const OperationLogs = lazy(() => import('@/pages/settings/operation-logs'))
const ApiMonitor = lazy(() => import('@/pages/api-monitor'))
const AppCosts = lazy(() => import('@/pages/settings/app-costs'))
const FreeChatConfigs = lazy(() => import('@/pages/settings/free-chat-configs'))
const AppABConfigs = lazy(() => import('@/pages/settings/app-ab-configs'))
const UploadConfigs = lazy(() => import('@/pages/settings/upload-configs'))
const AdminUploadConfig = lazy(() => import('@/pages/settings/admin-upload-config'))
const WorkflowTemplates = lazy(() => import('@/pages/settings/workflow-templates'))
const ImageLibrary = lazy(() => import('@/pages/settings/image-library'))
const AIProviders = lazy(() => import('@/pages/settings/ai-providers'))
const AIProviderManagerConfig = lazy(() => import('@/pages/settings/ai-provider-manager-config'))
const AIProviderCallLogs = lazy(() => import('@/pages/settings/ai-provider-call-logs'))
const AIProviderStats = lazy(() => import('@/pages/settings/ai-provider-stats'))
const MemoryTierPresets = lazy(() => import('@/pages/settings/memory-tier-presets'))
const PromptTemplates = lazy(() => import('@/pages/settings/prompt-templates'))
const RoomMemoriesAdmin = lazy(() => import('@/pages/settings/room-memories'))

// 图像配置
const ImageModels = lazy(() => import('@/pages/image-configs/models'))
const ImageStyles = lazy(() => import('@/pages/image-configs/styles'))
const ImageTags = lazy(() => import('@/pages/image-configs/tags'))
const ImageTypes = lazy(() => import('@/pages/image-configs/types'))

// 图像生成
const ImageGenTasks = lazy(() => import('@/pages/image-generation/tasks'))
const LoraLibrary = lazy(() => import('@/pages/image-generation/lora-library'))
const TranslationRecords = lazy(() => import('@/pages/image-generation/translation-records'))

// 用户管理
const AdminAccounts = lazy(() => import('@/pages/users/admins'))
const Roles = lazy(() => import('@/pages/users/roles'))
const PermissionsOverview = lazy(() => import('@/pages/users/permissions'))

// 403 无权限页
const Forbidden = lazy(() => import('@/pages/forbidden'))

// 个人中心
const Profile = lazy(() => import('@/pages/profile'))
const ChangePassword = lazy(() => import('@/pages/profile/password'))

// 审计
const PermissionChanges = lazy(() => import('@/pages/audit/permission-changes'))

// 404
const NotFound = lazy(() => import('@/pages/not-found'))

// 系统配置
const SystemConfigs = lazy(() => import('@/pages/settings/system-configs'))

// 推送管理
const PushStats = lazy(() => import('@/pages/push/stats'))
const PushTemplates = lazy(() => import('@/pages/push/templates'))
const PushTasksPage = lazy(() => import('@/pages/push/tasks'))
const PushTest = lazy(() => import('@/pages/push/test'))
const PushConfig = lazy(() => import('@/pages/push/config'))
const RecallLogs = lazy(() => import('@/pages/push/recall-logs'))

// 聊天管理
const Rooms = lazy(() => import('@/pages/rooms'))
const RoomMessagesPage = lazy(() => import('@/pages/rooms/messages-page'))
const ChatStatsPage = lazy(() => import('@/pages/rooms/chat-stats'))
const MessagePromptLogsPage = lazy(() => import('@/pages/prompt-logs/messages'))
const SummaryPromptLogsPage = lazy(() => import('@/pages/prompt-logs/summaries'))
const SmartReplyPromptLogsPage = lazy(() => import('@/pages/prompt-logs/smart-reply'))

// 其他
const AccessControl = lazy(() => import('@/pages/moderation/access-control'))
const JimengWhitelist = lazy(() => import('@/pages/jimeng-whitelist'))
const JimengWorkflowLogs = lazy(() => import('@/pages/jimeng-workflow-logs'))
const ComfyuiWorkflowLogs = lazy(() => import('@/pages/comfyui-workflow-logs'))

export function AppRoutes() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* 公开路由 */}
        <Route path="/login" element={<Login />} />

        {/* 受保护路由 */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="users" element={<Users />} />
          <Route path="users/attribution-coverage" element={
            <PermissionRoute permission="user.view"><AttributionCoverage /></PermissionRoute>
          } />
          <Route path="users/retention" element={
            <PermissionRoute permission="user.view"><UserRetention /></PermissionRoute>
          } />
          <Route path="users/geo" element={
            <PermissionRoute permission="payment.order.view"><UserGeo /></PermissionRoute>
          } />
          <Route path="users/currency-grants" element={
            <PermissionRoute permission="user.view"><CurrencyGrants /></PermissionRoute>
          } />
          <Route path="users/:id" element={
            <PermissionRoute permission="user.view"><UserDetail /></PermissionRoute>
          } />
          <Route path="users/admins" element={
            <PermissionRoute permission="admin.account.view"><AdminAccounts /></PermissionRoute>
          } />
          <Route path="users/roles" element={
            <PermissionRoute permission="admin.role.view"><Roles /></PermissionRoute>
          } />
          <Route path="users/permissions" element={
            <PermissionRoute permission="admin.role.view"><PermissionsOverview /></PermissionRoute>
          } />
          <Route path="reports" element={<Reports />} />
          <Route path="template-reports" element={<PermissionRoute permission="amspire.template_report.view"><TemplateReports /></PermissionRoute>} />

          {/* 角色管理 */}
          <Route path="characters" element={<Characters />} />
          <Route path="characters/audit" element={<CharacterAudit />} />
          <Route path="characters/categories" element={<CharacterCategories />} />
          <Route path="characters/new-characters" element={<PermissionRoute permission="character.taxonomy.manage"><CharacterNewCharacters /></PermissionRoute>} />
          <Route path="characters/tags" element={<CharacterTags />} />
          <Route path="characters/template-variables" element={<TemplateVariables />} />
          <Route path="characters/system-template-variables" element={<SystemTemplateVariables />} />
          <Route path="characters/stats" element={<CharacterStats />} />
          <Route path="characters/ai-configs" element={<CharacterAIConfigs />} />
          {/* 角色详情(新页签打开);放在静态子路由之后,React Router 会优先匹配静态路径 */}
          <Route path="characters/:id" element={
            <PermissionRoute permission="character.view"><CharacterDetail /></PermissionRoute>
          } />

          {/* 支付管理 */}
          <Route path="payment/products" element={<Products />} />
          <Route path="payment/orders" element={<Orders />} />
          <Route path="payment/subscriptions" element={<Subscriptions />} />
          <Route path="payment/refunds" element={<Refunds />} />
          <Route path="payment/fulfillments" element={<Fulfillments />} />
          <Route path="payment/webhook-logs" element={<WebhookLogs />} />
          <Route path="payment/payment-events" element={<PaymentEvents />} />
          <Route path="payment/analytics" element={<PaymentAnalytics />} />
          <Route path="payment/ltv" element={<PaymentLTV />} />
          <Route path="payment/cohort-orders" element={<CohortOrders />} />
          <Route path="payment/apple-pay-configs" element={<ApplePayConfigs />} />
          <Route path="payment/google-pay-configs" element={<GooglePayConfigs />} />
          <Route path="payment/appsflyer-configs" element={<AppsFlyerConfigs />} />
          <Route path="payment/thinkingdata-configs" element={<ThinkingDataConfigs />} />
          <Route path="payment/payment-channels" element={<PaymentChannels />} />
          <Route path="payment/payment-channel-groups" element={<PaymentChannelGroups />} />

          {/* 钻石统计 */}
          <Route path="diamond/overview" element={<DiamondOverview />} />
          <Route path="diamond/cohort" element={<DiamondCohort />} />
          <Route path="diamond/payment-analysis" element={<DiamondPaymentAnalysis />} />
          <Route path="diamond/ledger" element={<DiamondLedger />} />

          {/* 运营管理 */}
          <Route path="operations/rewards" element={<Rewards />} />
          <Route path="operations/activities" element={<Activities />} />
          <Route path="operations/activity-series" element={<ActivitySeries />} />
          <Route path="operations/announcements" element={<Announcements />} />
          <Route path="operations/notifications" element={<Notifications />} />
          <Route path="operations/inbox-messages" element={<InboxMessages />} />
          <Route path="operations/tasks" element={<Tasks />} />
          <Route path="operations/checkin" element={<Checkin />} />
          <Route path="operations/checkin-plans" element={<CheckinPlans />} />
          <Route path="operations/checkin-stats" element={<CheckinStats />} />
          <Route path="operations/invitation" element={<Invitation />} />
          <Route path="operations/email-bind-reward" element={<EmailBindReward />} />
          <Route path="operations/register-bonus" element={<RegisterBonus />} />
          <Route path="operations/vip" element={<Vip />} />
          <Route path="operations/trending-keywords" element={<TrendingKeywords />} />
          <Route path="operations/trending-keyword-stats" element={
            <PermissionRoute permission="op.trending.stats.view"><TrendingKeywordStats /></PermissionRoute>
          } />
          <Route path="operations/character-generation" element={<CharacterGeneration />} />
          <Route path="operations/smart-reply-configs" element={<SmartReplyConfigs />} />
          <Route path="operations/emotion-photo-configs" element={<EmotionPhotoConfigs />} />
          <Route path="operations/smart-reply-presets" element={<SmartReplyPresets />} />
          <Route path="operations/scheduled-tasks" element={<ScheduledTasks />} />

          {/* 系统设置 */}
          <Route path="settings/apps" element={<Apps />} />
          <Route path="settings/app-settings" element={<AppSettings />} />
          <Route path="settings/versions" element={<Versions />} />
          <Route path="settings/firebase" element={<Firebase />} />
          <Route path="settings/service-accounts" element={<ServiceAccounts />} />
          <Route path="settings/base-limits" element={<BaseLimits />} />
          <Route path="settings/alert-bots" element={<AlertBots />} />
          <Route path="settings/app-store-links" element={<AppStoreLinks />} />
          <Route path="settings/comfyui-servers" element={<ComfyuiServers />} />
          <Route path="settings/jimeng-configs" element={<JimengConfigs />} />
          <Route path="generation-v2/*" element={<PermissionRoute anyOf={['generation.v2.config.manage', 'generation.v2.task.view']}><GenerationV2 /></PermissionRoute>} />
          <Route path="generation-v2/tts-voices" element={<PermissionRoute permission="generation.v2.config.manage"><TTSVoices /></PermissionRoute>} />
          <Route path="generation-stats" element={<PermissionRoute permission="generation.v2.task.view"><GenerationStats /></PermissionRoute>} />
          <Route path="generation-analytics" element={<PermissionRoute permission="generation.v2.task.view"><GenerationAnalytics /></PermissionRoute>} />
          <Route path="amspire/library" element={<PermissionRoute anyOf={['amspire.library.view', 'amspire.library.manage', 'amspire.library.publish', 'amspire.library.distribute']}><AmspireLibrary /></PermissionRoute>} />
          <Route path="amspire" element={<PermissionRoute anyOf={['amspire.content.view', 'amspire.content.manage', 'amspire.ugc.moderate']}><AmspireAdmin /></PermissionRoute>} />
          <Route path="amspire/section-requests" element={<PermissionRoute anyOf={['amspire.content.view', 'amspire.content.manage']}><AmspireSectionRequests /></PermissionRoute>} />
          <Route path="settings/onelink-configs" element={<OnelinkConfigs />} />
          <Route path="settings/translation-configs" element={<TranslationConfigs />} />
          <Route path="settings/system-configs" element={<SystemConfigs />} />
          <Route path="settings/operation-logs" element={<OperationLogs />} />
          <Route path="system/api-monitor" element={<PermissionRoute permission="api_monitor.view"><ApiMonitor /></PermissionRoute>} />
          <Route path="settings/app-costs" element={<AppCosts />} />
          <Route path="settings/free-chat-configs" element={<FreeChatConfigs />} />
          <Route path="settings/app-ab-configs" element={<AppABConfigs />} />
          <Route path="settings/upload-configs" element={<UploadConfigs />} />
          <Route path="settings/admin-upload-config" element={<AdminUploadConfig />} />
          <Route path="settings/workflow-templates" element={<WorkflowTemplates />} />
          <Route path="settings/image-library" element={<ImageLibrary />} />
          <Route path="settings/ai-providers" element={<AIProviders />} />
          <Route path="settings/ai-provider-manager-config" element={<AIProviderManagerConfig />} />
          <Route path="settings/ai-provider-call-logs" element={<AIProviderCallLogs />} />
          <Route path="settings/ai-provider-stats" element={<AIProviderStats />} />
          <Route path="settings/memory-tier-presets" element={<MemoryTierPresets />} />
          <Route path="settings/prompt-templates" element={<PromptTemplates />} />
          <Route path="settings/room-memories" element={<RoomMemoriesAdmin />} />

          {/* 图像配置 */}
          <Route path="image-configs/models" element={<ImageModels />} />
          <Route path="image-configs/styles" element={<ImageStyles />} />
          <Route path="image-configs/tags" element={<ImageTags />} />
          <Route path="image-configs/types" element={<ImageTypes />} />

          {/* 图像生成 */}
          <Route path="image-generation/tasks" element={<ImageGenTasks />} />
          <Route path="image-generation/lora-library" element={<LoraLibrary />} />
          <Route path="image-generation/translation-records" element={<TranslationRecords />} />

          {/* 推送管理 */}
          <Route path="push/stats" element={<PushStats />} />
          <Route path="push/templates" element={<PushTemplates />} />
          <Route path="push/tasks" element={<PushTasksPage />} />
          <Route path="push/test" element={<PushTest />} />
          <Route path="push/config" element={<PushConfig />} />
          <Route path="push/recall-logs" element={<RecallLogs />} />

          {/* 聊天管理 */}
          <Route path="rooms" element={<Rooms />} />
          <Route path="rooms/messages" element={
            <PermissionRoute permission="room.message.view"><RoomMessagesPage /></PermissionRoute>
          } />
          <Route path="rooms/chat-stats" element={
            <PermissionRoute permission="room.view"><ChatStatsPage /></PermissionRoute>
          } />

          {/* 提示词日志 */}
          <Route path="prompt-logs/messages" element={
            <PermissionRoute permission="prompt.log.view"><MessagePromptLogsPage /></PermissionRoute>
          } />
          <Route path="prompt-logs/summaries" element={
            <PermissionRoute permission="prompt.log.view"><SummaryPromptLogsPage /></PermissionRoute>
          } />
          <Route path="prompt-logs/smart-replies" element={
            <PermissionRoute permission="prompt.log.view"><SmartReplyPromptLogsPage /></PermissionRoute>
          } />

          {/* 其他 */}
          <Route path="moderation/access-control" element={<AccessControl />} />
          <Route path="jimeng-whitelist" element={<JimengWhitelist />} />
          <Route path="jimeng-workflow-logs" element={<JimengWorkflowLogs />} />
          <Route path="comfyui-workflow-logs" element={<ComfyuiWorkflowLogs />} />

          {/* 个人中心(任何已登录管理员都可访问) */}
          <Route path="profile" element={<Profile />} />
          <Route path="profile/password" element={<ChangePassword />} />

          {/* 审计 */}
          <Route path="audit/permission-changes" element={
            <PermissionRoute permission="audit.permission_change.view"><PermissionChanges /></PermissionRoute>
          } />

          {/* 无权限页 */}
          <Route path="403" element={<Forbidden />} />

          {/* 受保护区域内的未匹配路径 — 在 AdminLayout 里显示 404,保留侧边栏 */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* 顶层未匹配(罕见,因为 ProtectedRoute 在 / 下已经兜底了) */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  )
}
